import { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { CLIENT_REPORTING_STOCKS } from './ClientReportingStocks';
import { CLIENT_REPORTING_BENCHMARK_SYMBOLS } from './clientReportingBenchmarkConfig';
import { quotesBySymbolFromResponse } from './clientReportingSidebarQuotes';
import {
  readSidebarQuotesCache,
  writeSidebarQuotesCache,
} from './clientReportingSidebarQuotesCache';
import { WORLD_MARKET_REGIONS } from './clientReportingWorldMarketsConfig';

/** Delayed watchlist quotes — not live ticks. Default 5 min between refreshes. */
export const CLIENT_REPORTING_SIDEBAR_POLL_MS = Number(
  process.env.REACT_APP_CLIENT_REPORTING_SIDEBAR_POLL_MS,
) || 300_000;

/** Standalone sidebar widgets: same slow poll + pause when tab hidden. */
export function useVisibilityAwareQuotePoll(loadQuotes, enabled = true, { immediate = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    let visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';

    const tick = () => {
      if (visible) loadQuotes();
    };

    const schedule = () => {
      if (timer) clearInterval(timer);
      if (!visible) return;
      timer = setInterval(tick, CLIENT_REPORTING_SIDEBAR_POLL_MS);
    };

    if (immediate) tick();
    schedule();

    const onVisibility = () => {
      visible = document.visibilityState !== 'hidden';
      if (visible) {
        tick();
        schedule();
      } else if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadQuotes, enabled, immediate]);
}

function regionById(regionId) {
  return WORLD_MARKET_REGIONS.find((r) => r.id === regionId) || WORLD_MARKET_REGIONS[0];
}

function regionHasQuotes(regionId, quotes) {
  const region = regionById(regionId);
  return region.symbols.every((symbol) => quotes[symbol]);
}

/**
 * Stocks + World Markets sidebar quotes with:
 * - stale-while-revalidate (localStorage + show last good data)
 * - slow background refresh (default 5 min, not 60s)
 * - pause polling while the browser tab is hidden
 * - world markets fetched per active region only (not all 17 symbols at once)
 */
export default function useClientReportingSidebarQuotes() {
  const cached = readSidebarQuotesCache();
  const [stockQuotes, setStockQuotes] = useState(() => cached?.stocks || {});
  const [worldQuotes, setWorldQuotes] = useState(() => cached?.world || {});
  const [benchmarkQuotes, setBenchmarkQuotes] = useState(() => cached?.benchmarks || {});
  const [stockLoading, setStockLoading] = useState(false);
  const [worldLoading, setWorldLoading] = useState(false);
  const [warning, setWarning] = useState('');
  const [worldRegionId, setWorldRegionId] = useState('americas');

  const loadedWorldRegionsRef = useRef(new Set());
  const visibleRef = useRef(typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const lastRefreshRef = useRef(cached?.ts || 0);
  const stockQuotesRef = useRef(stockQuotes);
  const worldQuotesRef = useRef(worldQuotes);
  const benchmarkQuotesRef = useRef(benchmarkQuotes);
  stockQuotesRef.current = stockQuotes;
  worldQuotesRef.current = worldQuotes;
  benchmarkQuotesRef.current = benchmarkQuotes;

  const persistCache = useCallback((stocks, world, benchmarks) => {
    writeSidebarQuotesCache({ stocks, world, benchmarks });
    lastRefreshRef.current = Date.now();
  }, []);

  const fetchStockQuotes = useCallback(async ({ background = false } = {}) => {
    if (!background) setStockLoading(true);
    try {
      const symbols = CLIENT_REPORTING_STOCKS.map((stock) => stock.symbol).join(',');
      const res = await fetch(API_ENDPOINTS.stockQuotes({ symbols }), {
        headers: getBearerAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.warning || 'Failed to load stock quotes');
      }
      const nextStocks = quotesBySymbolFromResponse(data.markets);
      setStockQuotes(nextStocks);
      if (data.warning) setWarning(data.warning);
      else if (!background) setWarning('');
      persistCache(nextStocks, worldQuotesRef.current, benchmarkQuotesRef.current);
      return nextStocks;
    } catch (err) {
      console.error('[client-reporting-sidebar-stocks]', err);
      if (!background && !Object.keys(stockQuotesRef.current).length) {
        setWarning(err.message || 'Stock quotes unavailable.');
      }
      return null;
    } finally {
      if (!background) setStockLoading(false);
    }
  }, [persistCache]);

  const fetchWorldRegion = useCallback(async (regionId, { background = false } = {}) => {
    const region = regionById(regionId);
    if (!region) return null;
    if (!background) setWorldLoading(true);
    try {
      const res = await fetch(
        API_ENDPOINTS.marketQuotes({ symbols: region.symbols.join(',') }),
        { headers: getBearerAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.warning || 'Failed to load market quotes');
      const regionQuotes = quotesBySymbolFromResponse(data.markets);
      setWorldQuotes((prev) => {
        const merged = { ...prev, ...regionQuotes };
        persistCache(stockQuotesRef.current, merged, benchmarkQuotesRef.current);
        return merged;
      });
      loadedWorldRegionsRef.current.add(regionId);
      if (data.warning) setWarning(data.warning);
      else if (!background) setWarning('');
      return regionQuotes;
    } catch (err) {
      console.error('[client-reporting-sidebar-world]', err);
      if (!background && !Object.keys(worldQuotesRef.current).length) {
        setWarning(err.message || 'Market quotes unavailable.');
      }
      return null;
    } finally {
      if (!background) setWorldLoading(false);
    }
  }, [persistCache]);

  const fetchBenchmarkQuotes = useCallback(async ({ background = false } = {}) => {
    if (!CLIENT_REPORTING_BENCHMARK_SYMBOLS.length) return null;
    if (!background) setWorldLoading(true);
    try {
      const res = await fetch(
        API_ENDPOINTS.marketQuotes({ symbols: CLIENT_REPORTING_BENCHMARK_SYMBOLS.join(',') }),
        { headers: getBearerAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.warning || 'Failed to load benchmark quotes');
      const nextBenchmarks = quotesBySymbolFromResponse(data.markets);
      setBenchmarkQuotes(nextBenchmarks);
      persistCache(stockQuotesRef.current, worldQuotesRef.current, nextBenchmarks);
      if (data.warning) setWarning(data.warning);
      else if (!background) setWarning('');
      return nextBenchmarks;
    } catch (err) {
      console.error('[client-reporting-sidebar-benchmarks]', err);
      if (!background && !Object.keys(benchmarkQuotesRef.current).length) {
        setWarning(err.message || 'Benchmark quotes unavailable.');
      }
      return null;
    } finally {
      if (!background) setWorldLoading(false);
    }
  }, [persistCache]);

  const refreshAllLoaded = useCallback(async ({ background = true } = {}) => {
    await fetchStockQuotes({ background });
    await fetchBenchmarkQuotes({ background });
    const regions = Array.from(loadedWorldRegionsRef.current);
    await Promise.all(regions.map((id) => fetchWorldRegion(id, { background })));
  }, [fetchStockQuotes, fetchBenchmarkQuotes, fetchWorldRegion]);

  // Initial stock + benchmark quote load — show cache immediately, refresh in background.
  useEffect(() => {
    const hasCachedStocks = Boolean(cached?.stocks && Object.keys(cached.stocks).length);
    const hasCachedBenchmarks = Boolean(cached?.benchmarks && Object.keys(cached.benchmarks).length);
    fetchStockQuotes({ background: hasCachedStocks });
    fetchBenchmarkQuotes({ background: hasCachedBenchmarks });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // World markets: fetch only the active region's symbols (5–6, not all 17).
  useEffect(() => {
    const hasCachedRegion = regionHasQuotes(worldRegionId, worldQuotesRef.current);
    fetchWorldRegion(worldRegionId, { background: hasCachedRegion });
  }, [worldRegionId, fetchWorldRegion]);

  // Slow refresh + pause while tab hidden.
  useEffect(() => {
    let timer = null;

    const schedule = () => {
      if (timer) clearInterval(timer);
      if (!visibleRef.current) return;
      timer = setInterval(() => {
        if (visibleRef.current) refreshAllLoaded({ background: true });
      }, CLIENT_REPORTING_SIDEBAR_POLL_MS);
    };

    const onVisibility = () => {
      visibleRef.current = document.visibilityState !== 'hidden';
      if (visibleRef.current) {
        const stale = Date.now() - lastRefreshRef.current > CLIENT_REPORTING_SIDEBAR_POLL_MS;
        if (stale) refreshAllLoaded({ background: true });
        schedule();
      } else if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshAllLoaded]);

  const sidebarQuotesBySymbol = { ...stockQuotes, ...worldQuotes, ...benchmarkQuotes };

  return {
    stockQuotes,
    worldQuotes,
    benchmarkQuotes,
    sidebarQuotesBySymbol,
    stockLoading,
    worldLoading,
    warning,
    worldRegionId,
    setWorldRegionId,
  };
}
